import { styled } from "goober";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useNearViewport } from "../../hooks/useNearViewport";
import type { SkillItem } from "../../hooks/useSlashSkills";
import type {
  MentionConnector,
  MentionConnectorSection,
} from "../../hooks/useMentions";
import { Button } from "../Button";
import { ConnectorLogo } from "../ConnectorLogo";
import { Dropdown } from "../Dropdown";
import { routes, useMakeUrl } from "../../lib/urls";
import { Icon } from "../Icon";
import { Keycap } from "../Keycap";
import { Text } from "../Text";

const AddButton = styled(Button)`
  border-radius: 50%;
  flex-shrink: 0;
  & > span {
    transition: transform var(--motion-base) var(--ease-out);
  }
  &:hover:not(:disabled) > span {
    transform: rotate(90deg);
  }
  [data-dropdown-open="true"] &:not(:disabled) > span {
    transform: rotate(135deg);
  }
`;

const LogoSlot = styled("span")`
  display: inline-flex;
  width: 16px;
  height: 16px;
`;

// Theme-aware logos read computed style on mount, which stalls for seconds
// across the full catalog, so only mount the ones scrolled into view.
function LazyConnectorLogo({ connector }: { connector: MentionConnector }) {
  const ref = useRef<HTMLSpanElement>(null);
  const near = useNearViewport(ref, { once: true });
  return (
    <LogoSlot ref={ref}>
      {near && <ConnectorLogo template={connector} size={16} />}
    </LogoSlot>
  );
}

const SKILL_BROWSE_COUNT = 8;
const SKILL_SEARCH_LIMIT = 25;

function skillMatches(
  skills: readonly SkillItem[],
  query: string,
  showAll: boolean
) {
  if (query === "")
    return showAll ? skills : skills.slice(0, SKILL_BROWSE_COUNT);
  const q = query.toLowerCase();
  return skills
    .filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q)
    )
    .slice(0, SKILL_SEARCH_LIMIT);
}

function showAllItem(visible: boolean, label: string, onClick: () => void) {
  return visible
    ? [
        {
          type: "button" as const,
          left: <Icon name="chevron-down" size={12} />,
          label,
          muted: true,
          keepOpen: true,
          onClick,
        },
      ]
    : [];
}

const countItems = (sections: readonly MentionConnectorSection[]) =>
  sections.reduce((total, section) => total + section.items.length, 0);

type ComposerPickers = {
  skills: readonly SkillItem[];
  onAddSkill: (skill: SkillItem) => void;
  searchConnectors: (
    query: string,
    browseAll: boolean
  ) => readonly MentionConnectorSection[];
  onAddConnector: (connector: MentionConnector) => void;
  onCreateSkill?: () => void;
  onPrefetch: () => void;
};

export function ComposerAddMenu({
  onAddFiles,
  pickers,
  disabled = false,
}: {
  onAddFiles: (files: File[]) => void;
  pickers: ComposerPickers;
  disabled?: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const openFilePicker = () => fileInputRef.current?.click();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuKey, setMenuKey] = useState(0);

  const onShortcut = useEffectEvent((e: KeyboardEvent) => {
    const target = e.target instanceof Element ? e.target : null;
    const inOwnMenu = menuOpen && target?.closest("[data-popover]") != null;
    if (
      !inOwnMenu &&
      (document.querySelector("dialog[open]") ||
        target?.closest('[role="dialog"], [data-popover]'))
    )
      return;
    e.preventDefault();
    if (menuOpen) {
      // Dropdown has no imperative close, so remount it closed.
      setMenuOpen(false);
      setMenuKey((key) => key + 1);
    }
    openFilePicker();
  });

  useEffect(() => {
    if (disabled) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.defaultPrevented ||
        e.repeat ||
        e.isComposing ||
        !(e.metaKey || e.ctrlKey) ||
        e.shiftKey ||
        e.altKey ||
        e.code !== "KeyU"
      )
        return;
      onShortcut(e);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [disabled]);

  return (
    <>
      {disabled ? (
        <AddButton aria-label="Add" icon="plus" variant="subtle" disabled />
      ) : (
        <PickerMenu
          key={menuKey}
          {...pickers}
          onAddFiles={openFilePicker}
          onOpenChange={setMenuOpen}
        />
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        disabled={disabled}
        onChange={(event) => {
          const { files } = event.currentTarget;
          if (files?.length) onAddFiles(Array.from(files));
          event.currentTarget.value = "";
        }}
      />
    </>
  );
}

function PickerMenu({
  skills,
  onAddSkill,
  searchConnectors,
  onAddConnector,
  onCreateSkill,
  onPrefetch,
  onAddFiles,
  onOpenChange,
}: ComposerPickers & {
  onAddFiles: () => void;
  onOpenChange: (open: boolean) => void;
}) {
  const makeUrl = useMakeUrl();
  const [showAllConnectors, setShowAllConnectors] = useState(false);
  const [showAllSkills, setShowAllSkills] = useState(false);

  return (
    <Dropdown
      opener={
        <span onPointerEnter={onPrefetch} onFocus={onPrefetch}>
          <AddButton aria-label="Add" icon="plus" variant="subtle" />
        </span>
      }
      align="top-left"
      minWidth={180}
      onOpenChange={(open) => {
        onOpenChange(open);
        if (open) return;
        setShowAllConnectors(false);
        setShowAllSkills(false);
      }}
      sections={[
        {
          items: [
            {
              type: "button",
              left: <Icon name="paperclip" size={16} />,
              label: "Add files",
              right: (
                <div style={{ display: "flex", gap: 2 }}>
                  <Keycap style={{ padding: "2px 4px" }}>
                    <Text variant="text-smaller-md">⌘</Text>
                  </Keycap>
                  <Keycap style={{ padding: "2px 4px" }}>
                    <Text variant="text-smaller-md">U</Text>
                  </Keycap>
                </div>
              ),
              onClick: onAddFiles,
            },
            {
              type: "submenu",
              left: <Icon name="api-key-custom" size={16} />,
              label: "Add connector",
              searchable: true,
              submenu: (query) => {
                const results = searchConnectors(query, showAllConnectors);
                const canShowAll = query === "" && !showAllConnectors;
                const total = canShowAll
                  ? countItems(searchConnectors("", true))
                  : 0;
                const hasMore = total > countItems(results);
                return [
                  ...results.map((section, index) => ({
                    label: section.label,
                    items: [
                      ...section.items.map((c) => ({
                        type: "button" as const,
                        key: `${c.identifier}:${c.name}`,
                        left:
                          showAllConnectors && query === "" ? (
                            <LazyConnectorLogo connector={c} />
                          ) : (
                            <ConnectorLogo template={c} size={16} />
                          ),
                        label: c.name,
                        onClick: () =>
                          onAddConnector({
                            name: c.name,
                            identifier: c.identifier,
                          }),
                      })),
                      ...showAllItem(
                        canShowAll && hasMore && index === results.length - 1,
                        `Show all ${total.toLocaleString("en-US")} connectors`,
                        () => setShowAllConnectors(true)
                      ),
                    ],
                  })),
                  {
                    items: results.some((r) => r.items.length > 0)
                      ? []
                      : [
                          {
                            type: "link" as const,
                            to: makeUrl(routes.connectors.browse, {
                              q: query,
                            }),
                            left: <Icon name="api-key-custom" size={16} />,
                            label: "Browse connectors",
                          },
                          {
                            type: "link" as const,
                            to: makeUrl(routes.connectors.browse.create, {
                              identifier: "apiKey",
                            }),
                            left: <Icon name="plus" size={16} />,
                            label: "Create API key connector",
                          },
                        ],
                  },
                ];
              },
            },
            {
              type: "submenu",
              left: <Icon name="skills" size={16} />,
              label: "Add skills",
              searchable: true,
              submenu: (query) => {
                const matches = skillMatches(skills, query, showAllSkills);
                return [
                  {
                    items: [
                      ...matches.map((skill) => ({
                        type: "button" as const,
                        key: skill.id,
                        disabled: skill.disabled,
                        left: <Icon name="skills" size={16} />,
                        label: skill.name,
                        subtitle: skill.description ? (
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                            ellipsis
                          >
                            {skill.description}
                          </Text>
                        ) : undefined,
                        onClick: () => onAddSkill(skill),
                      })),
                      ...showAllItem(
                        query === "" &&
                          !showAllSkills &&
                          skills.length > SKILL_BROWSE_COUNT,
                        `Show all ${skills.length.toLocaleString("en-US")} skills`,
                        () => setShowAllSkills(true)
                      ),
                    ],
                  },
                  {
                    items:
                      matches.length > 0
                        ? []
                        : [
                            {
                              type: "link" as const,
                              to: makeUrl(routes.skills.list, { q: query }),
                              left: <Icon name="skills" size={16} />,
                              label: "Browse skills",
                            },
                            ...(onCreateSkill
                              ? [
                                  {
                                    type: "button" as const,
                                    left: <Icon name="plus" size={16} />,
                                    label: "Create a skill",
                                    onClick: onCreateSkill,
                                  },
                                ]
                              : []),
                          ],
                  },
                ];
              },
            },
          ],
        },
      ]}
    />
  );
}
